import React, {
	createContext,
	useCallback,
	useContext,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react';
import type {ReactAction as Action, ReactAction} from '@netx/core/bootstrap';
import {
	renderChildrenOrChildrenFunction,
	type IContextChildren,
	type IContextRef,
} from '@netx/core-next';

import ContextMenu, {type ContextMenuOptions} from './ContextMenu';

export type ContextMenuServiceContextType = {
	isOpen: boolean;
	trigger: (options: ContextMenuOptions) => Promise<Action | undefined>;
	close: (action?: ReactAction) => void;
};

// no default, must be used within a `ContextMenuServiceProvider`
const ContextMenuServiceContext = createContext<
	ContextMenuServiceContextType | undefined
>(undefined);

export type ContextMenuServiceProviderRef =
	IContextRef<ContextMenuServiceContextType>;

export type ContextMenuServiceProviderProps = {
	ref?: ContextMenuServiceProviderRef;
	children: IContextChildren<ContextMenuServiceContextType>;
};

export const useContextMenu = () => {
	const context = useContext(ContextMenuServiceContext);
	if (context === undefined) {
		throw new Error(
			'`useContextMenu` must be used within a `ContextMenuServiceProvider`',
		);
	}
	return context;
};

const ContextMenuServiceProviderInner = (
	{children}: ContextMenuServiceProviderProps,
	ref: ContextMenuServiceProviderRef,
) => {
	const [open, setOpen] = useState(false);
	const [contextMenuState, setContextMenuState] =
		useState<ContextMenuOptions>();

	const awaitingPromiseRef = useRef<{
		resolve(action?: Action): void;
		reject(): void;
	}>();

	const openContextMenu = useCallback((options: ContextMenuOptions) => {
		setContextMenuState(options);
		setOpen(true);
		return new Promise<Action | undefined>((resolve, reject) => {
			awaitingPromiseRef.current = {resolve, reject};
		});
	}, []);

	const handleExited = useCallback((action?: Action) => {
		awaitingPromiseRef.current?.resolve(action);
		awaitingPromiseRef.current = undefined;
		setContextMenuState(undefined);
		setOpen(false);
	}, []);

	const contextValue = useMemo<ContextMenuServiceContextType>(
		() => ({
			trigger: openContextMenu,
			isOpen: open,
			close: handleExited,
		}),
		[openContextMenu, open],
	);

	useImperativeHandle(ref, () => contextValue, [contextValue]);

	return (
		<ContextMenuServiceContext.Provider value={contextValue}>
			{renderChildrenOrChildrenFunction(children, contextValue)}
			<ContextMenu
				open={open}
				onClose={() => {
					setOpen(false);
				}}
				onExited={handleExited}
				{...(contextMenuState as ContextMenuOptions)}
			/>
		</ContextMenuServiceContext.Provider>
	);
};

export const ContextMenuServiceProviderPrimitive = React.forwardRef<
	ContextMenuServiceContextType,
	ContextMenuServiceProviderProps
>(ContextMenuServiceProviderInner);

/**
 * ContextMenu service provider
 * @example
 * <ContextMenuServiceProvider ref={contextMenuRef}>
 * 	...
 * </ContextMenuServiceProvider>
 * @example
 * <ContextMenuServiceProvider ref={contextMenuRef}>
 * 	{(contextMenu) => (
 * 		...
 * 	)}
 * </ContextMenuServiceProvider>
 */
export const ContextMenuServiceProvider = React.memo(
	ContextMenuServiceProviderPrimitive,
);

export default ContextMenuServiceProvider;
